// TODO this should be split up
import {
  type ButtonHTMLAttributes,
  type DetailedHTMLProps,
  type FormHTMLAttributes,
  type HTMLAttributes,
  type ImgHTMLAttributes,
  type InputHTMLAttributes,
} from 'react';
import type React from 'react';
import type IdentityConnectionProvider from './constants/IdentityConnectionProvider.enum';
import type UserRole from './constants/UserRole.enum';
import type ModelType from './constants/challengeModel/ModelType.enum';
import type KanbanPhase from './constants/KanbanPhase.enum';
import type FieldType from './constants/challengeModel/FieldType.enum';
import type ContentGroupType from './constants/challengeModel/ContentGroupType.enum';
import { type DraftFunction } from 'use-immer';
import {
  type SelectProps as MuiSelectProps,
  type SelectVariants,
} from '@mui/material';

export type UserSummary = {
  name: string;
  email: string;
  picture: string | null;
};

// TODO
export type User = {
  email: string;
  name: string;
  firstName: string;
  picture: string | null;
  role: UserRole;
  groups: number[];
  managedGroups: number[];
  perspectiveCohortId: number | null;
};

export type UserWithId = User & {
  id: number;
};

export type AuthenticatedUser = User & {
  perspectiveCohort: { id: number } | null;
};

export type RowWithoutRelations = {
  id: number;
  name: string;
  position: number;
};

export type Row = RowWithoutRelations & {
  challenges: number[];
};

export type ChallengeSummary = {
  id: number;
  name: string;
  phase: KanbanPhase;
  plannedStartDate: string | null;
  plannedEndDate: string | null;
  color: string | null;
  thumbnail: UploadedImage | null;
  users: UserSummary[];
  invitees: UserSummary[];
  worlds: string[];
  tags: Tag[];
  // TODO is tasksummary correct here? also record/arr thing?
  tasks: Record<number, TaskSummary> | TaskSummary[];
  createdAt: string;
  _count: {
    coachComments: number;
  };
};

export type ChallengeWithoutRelations = {
  id: number;
  uuid: string;
  name: string;
  phase: KanbanPhase;
  isPublic: boolean;
  color: string | null;
  startDate: string | null;
  endDate: string | null;
  plannedStartDate: string | null;
  plannedEndDate: string | null;
  modelId: number | null;
  createdAt: string;
};

export type Challenge = ChallengeWithoutRelations & {
  author: UserSummary | null;
  users: UserSummary[];
  invitees: UserSummary[];
  fieldValues: {
    [fieldId: number]: FieldValue;
  };
  logEntries: Record<number, LogEntry>;
  // TODO is tasksummary correct here?
  tasks: Record<number, TaskSummary> | TaskSummary[];
  coachComments: CoachComment[];
  usersCount: number;
  rubrics: RubricsPortfolio;
  thumbnail: UploadedImage | null;
  tags: Tag[];
};

// TODO in these types handle anonymous handling!!
export type TaskSummary = {
  id: number;
  uuid: string;
  name: string;
  phase: KanbanPhase;
  plannedStartDate: string | null;
  plannedEndDate: string | null;
  color: string | null;
  thumbnail: UploadedImage | null;
  assignees: UserSummary[];
  // TODO or is it the full perspective? Or rename to
  // PerspectiveWithoutRelations?
  directlyAttachedPerspectives: PerspectiveSummary[];
  indirectlyAttachedPerspectives: PerspectiveSummary[];
  // TODO is this correct? Is this field really there?
  attachedPerspectiveSublevels: PerspectiveSublevelSummary[];
  worlds: string[];
  tags: Tag[];
  createdAt: string;
  _count: {
    coachComments: number;
  };
};

export type TaskWithoutRelations = {
  id: number;
  uuid: string;
  name: string;
  phase: KanbanPhase;
  isPublic: boolean;
  color: string | null;
  startDate: string | null;
  endDate: string | null;
  plannedStartDate: string | null;
  plannedEndDate: string | null;
  modelId: number | null;
  createdAt: string;
};

export type Task = TaskWithoutRelations & {
  challenge: {
    name: string | null;
  };
  author: UserSummary;
  assignees: UserSummary[];
  assigneesCount: number;
  fieldValues: {
    [fieldId: number]: FieldValue;
  };
  logEntries: Record<number, LogEntry>;
  coachComments: CoachComment[];
  rubrics: RubricsPortfolio;
  directlyAttachedPerspectives: Record<number, PerspectiveSummary>;
  indirectlyAttachedPerspectives: Record<number, PerspectiveSummary>;
  attachedPerspectiveSublevels: Record<number, PerspectiveSublevelSummary>;
  thumbnail: UploadedImage | null;
  tags: Tag[];
};

export type ChallengePortfolio = {
  rows: Record<string, Row>;
  challenges: Record<string, ChallengeSummary>;
};

export enum FileStatus {
  ANTIVIRUS_UNSCANNED = 'ANTIVIRUS_UNSCANNED',
  ANTIVIRUS_QUARANTINED = 'ANTIVIRUS_QUARANTINED',
  ANTIVIRUS_CLEAN = 'ANTIVIRUS_CLEAN',
  COMPRESSING = 'COMPRESSING',
  COMPRESSION_COMPLETE = 'COMPRESSION_COMPLETE',
  INAPPROPRIATE_CONTENT_DETECTION_STARTED = 'INAPPROPRIATE_CONTENT_DETECTION_STARTED',
  INAPPROPRIATE_CONTENT_DETECTED = 'INAPPROPRIATE_CONTENT_DETECTED',
  READY = 'READY',
}

export enum CloudVisionLikelihood {
  UNKNOWN = 0,
  VERY_UNLIKELY = 1,
  UNLIKELY = 2,
  POSSIBLE = 3,
  LIKELY = 4,
  VERY_LIKELY = 5,
}

export type CloudVisionSafeSearchDetectionResult = {
  adult: CloudVisionLikelihood;
  spoof: CloudVisionLikelihood;
  medical: CloudVisionLikelihood;
  violence: CloudVisionLikelihood;
  racy: CloudVisionLikelihood;
};

type DatabaseFile = {
  id: number;
  status: FileStatus;
  originalFilename: string;
  storageFilename: string;
  thumbnailStorageFilename: string | null;
  cloudVisionSafeSearchDetectionResult: string | null;
  createdAt: string;
  updatedAt: string;
};

export type UploadedImage = {
  id: number;
  fileId: number;
  file: DatabaseFile;
  createdAt: string;
  updatedAt: string;
};

export enum ExternalFileSource {
  GOOGLE_DRIVE = 'GOOGLE_DRIVE',
  MICROSOFT_ONEDRIVE = 'MICROSOFT_ONEDRIVE',
}

export type ExternalFile = {
  source: ExternalFileSource;
  externalId: string;
  filename: string;
  url: string;
  mimeType: string | undefined;
};

export type DatabaseExternalFile = {
  id: number;
  externalFileId: number;
  externalFile: Omit<ExternalFile, 'mimeType'> & {
    mimeType: string | null;
  };
  createdAt: string;
  updatedAt: string;
};

export type FieldValue = {
  typeSpecificValues: {
    [type: string]: {
      type: FieldType;
      value: string;
    };
  };
  images: Record<number, UploadedImage>;
  externalFiles: Record<number, DatabaseExternalFile>;
};

export type LogEntry = {
  id: number;
  content: string;
  author: UserSummary | null;
  fieldId: number;
  images: Record<number, UploadedImage>;
  externalFiles: Record<number, DatabaseExternalFile>;
  createdAt: string;
};

export type LogEntryWithoutFiles = Omit<LogEntry, 'images' | 'externalFiles'>;

export type CoachComment = {
  id: number;
  content: string;
  isRead: boolean;
  author: UserSummary | null;
  fieldId: number | null;
  logEntryId: number | null;
  createdAt: string;
  updatedAt: string;
};

export type IdPositionTuple = [number, number];

export type LinkButtonConfig = {
  link?: string;
  iconUrl?: string;
  label?: string;
};

export type AdditionalFieldConfig = {
  colors?: string[];
  linkButton?: LinkButtonConfig;
  logEntryName?: string;
  options?: {
    [uuid: string]: {
      name: string;
    };
  };
  multipleOptionsSelectable?: boolean;
};

// TODO more scoped names?
export type Field = {
  id: number;
  name: string | null;
  type: FieldType;
  position: number;
  visibleFrom: string | null;
  visibleUntil: string | null;
  info: string | null;
  additionalConfig: AdditionalFieldConfig;
  contentGroup: ContentGroupType;
  groups: { id: number }[];
  parts: Omit<Part, 'groups'>[];
};

export type Section = {
  id: number;
  name: string;
  position: number;
  visibleFrom: string;
  visibleUntil: string;
  fields: Field[];
  groups: { id: number }[];
};

export type Model = {
  id: number;
  type: ModelType;
  name: string | null;
  isDefault: boolean;
  deactivatedAt: string | null;
  sections: Section[];
};

export type ChallengeModels = {
  readonly [ModelType.CHALLENGE]: Record<number, Model>;
  readonly [ModelType.TASK]: Record<number, Model>;
};

export type EditorSectionWithoutRelations = {
  id: number;
  name: string;
  position: number;
  visibleFrom: string | null;
  visibleUntil: string | null;
};

export type EditorSection = EditorSectionWithoutRelations & {
  groups: { id: number }[];
  fields: { id: number }[];
};

export type EditorFieldWithoutRelations = {
  id: number;
  name: string | null;
  type: FieldType;
  position: number;
  visibleFrom: string | null;
  visibleUntil: string | null;
  info: string | null;
  additionalConfig: AdditionalFieldConfig;
  contentGroup: ContentGroupType;
};

export type EditorField = EditorFieldWithoutRelations & {
  groups: { id: number }[];
  parts: (EditorPartWithoutRelations & {
    levels: EditorLevelWithoutRelations[];
  })[];
};

export type EditorModelWithoutRelations = {
  id: number;
  type: ModelType;
  name: string | null;
  isDefault: boolean;
  deactivatedAt: string | null;
};

export type EditorModel = EditorModelWithoutRelations & {
  sections: Record<number, EditorSection>;
  fields: Record<number, EditorField>;
};

export type Level = {
  id: number;
  title: string | null;
  position: number;
  description: string;
  partId: number;
};

export type Part = {
  id: number;
  name: string;
  position: number;
  minimumRole: UserRole;
  groups: { id: number }[];
  levels: Level[];
};

export type Subtopic = {
  id: number;
  name: string;
  position: number;
  minimumRole: UserRole;
  groups: { id: number }[];
  parts: Part[];
};

export type Topic = {
  id: number;
  name: string;
  position: number;
  minimumRole: UserRole;
  groups: { id: number }[];
  subtopics: Subtopic[];
};

export type PortfolioTemplates = Topic[];

export type EditorLevelWithoutRelations = {
  id: number;
  title: string | null;
  position: number;
  description: string;
  partId: number;
};

export type EditorLevel = EditorLevelWithoutRelations;

export type EditorPartWithoutRelations = {
  id: number;
  name: string;
  position: number;
  minimumRole: MinimumRole;
  subtopicId: number;
};

export type EditorPart = EditorPartWithoutRelations & {
  groups: { id: number }[];
  levels: { id: number; position?: number }[];
};

export type EditorSubtopicWithoutRelations = {
  id: number;
  name: string;
  position: number;
  minimumRole: MinimumRole;
  topicId: number;
};

export type EditorSubtopic = EditorSubtopicWithoutRelations & {
  groups: { id: number }[];
  parts: { id: number; position?: number }[];
};

export type EditorTopicWithoutRelations = {
  id: number;
  name: string;
  position: number;
  minimumRole: MinimumRole;
};

export type EditorTopic = EditorTopicWithoutRelations & {
  groups: { id: number }[];
  subtopics: { id: number; position?: number }[];
};

export type EditorPortfolioTemplates = {
  topics: Record<number, EditorTopic>;
  subtopics: Record<number, EditorSubtopic>;
  parts: Record<number, EditorPart>;
  levels: Record<number, EditorLevel>;
};

export type MinimumRole = UserRole.STUDENT | UserRole.COACH | UserRole.EDITOR;

export type RubricProofLink = {
  id: number;
  link: string;
  levelId: number;
  createdAt: string;
  updatedAt: string;
};

export type LevelProgressNote = {
  id: number;
  content: string;
  studentProgress: number;
  coachProgress: number;
  levelId: number;
  user?: UserSummary | null;
  createdAt: string;
  updatedAt: string;
};

export type LevelProgressCoachComment = {
  id: number;
  content: string;
  isRead: boolean;
  studentProgress: number;
  coachProgress: number;
  author: UserSummary | null;
  levelId: number;
  createdAt: string;
  updatedAt: string;
};

export type RubricProgressObject = {
  student?: number;
  coach?: number;
};

export type RubricsPortfolio = {
  focus: {
    [partId: number]: boolean;
  };
  proofLinks: {
    [levelId: number]: Record<number, RubricProofLink | string>;
  };
  proofImages?: {
    [levelId: number]: Record<number, UploadedImage>;
  };
  proofExternalFiles?: {
    [levelId: number]: Record<number, DatabaseExternalFile>;
  };
  levelProgressNotes?: {
    [levelId: number]: Record<string, LevelProgressNote>;
  };
  levelProgressCoachComments?: {
    [levelId: number]: Record<string, LevelProgressCoachComment>;
  };
  progress: {
    [partId: number]: RubricProgressObject;
  };
};

export type AllowedEmail = readonly [string, string];

export type CustomChallengeNaming = {
  readonly enabled: boolean;
  readonly customChallengeNameSingular?: string;
  readonly customChallengeNamePlural?: string;
};

export type InstanceConfig = {
  readonly allowedEmails: AllowedEmail[];
  readonly authProvider: IdentityConnectionProvider;
  readonly enablePerspectiveModule: boolean;
  readonly useReversedSliders: boolean;
  readonly enableUserSwitcherArrows: boolean;
  readonly useProfilePictures: boolean;
  readonly enableAggressiveRubricCollapsing: boolean;
  readonly customChallengeNaming: CustomChallengeNaming;
};

export type SchoolSummary = {
  id: number;
  reference: string;
  displayName: string;
  logoFilename: string | null;
};

export type School = SchoolSummary & {
  instanceConfig: InstanceConfig;
};

export type RubricsPortfolioSnapshotsOverview = {
  years: number[];
  //  TODO we're currently always using string as record key, does this make
  // sense?
  weeks: Record<string, number[]>;
};

export type RubricsPortfolioSnapshot = {
  id: number;
  year: number;
  week: number;
  snapshot: string;
  createdAt: string;
};

export type RubricsPortfolioSnapshotsForWeek = {
  [week: number]: RubricsPortfolioSnapshot;
};

export type RubricsPortfolioSnapshotsObject = {
  [year: number]: RubricsPortfolioSnapshotsForWeek;
};

// TODO should be deprecated in favour of ReactContextWithProvider
export type ReactContextWithAutomatedProvider<T> = React.Context<T> & {
  AutomatedProvider: React.ElementType;
};

export type Group = {
  id: number;
  name: string;
  memberCount: number;
  managerCount: number;
};

export type SetComplexStateAction<T> = (
  stateUpdateFunction: (oldState: T) => T,
) => void;

export type SetComplexImmerStateAction<T> = (
  stateUpdateFunction: DraftFunction<T>,
) => void;

export type PerspectiveCohort = {
  id: number;
  name: string;
};

export type PerspectiveExplanation = {
  id: number;
  text: string | null;
};

export type Perspective = {
  id: number;
  name: string;
  color: string;
  icon: string | null;
  explanation: PerspectiveExplanation;
};

export type PerspectiveWithSublevelsInfo = Perspective & {
  sublevels: { id: number }[];
  // In the attachment sublevel-tree view, the `sublevels` array only includes
  // the DIRECT child sublevels instead of all sublevels, so we fall back to
  // this.
  sublevelIdsIncludingNested?: number[];
};

export type PerspectiveSummary = {
  id: number;
  name: string;
  color: string;
  icon: string | null;
};

export type PerspectiveSublevel = {
  id: number;
  name: string;
  explanation: PerspectiveExplanation;
};

export type PerspectiveSublevelSummary = {
  id: number;
};

export type PerspectiveSublevelWithChildren = PerspectiveSublevel & {
  children: PerspectiveSublevelWithChildren[];
  childIdsIncludingNested: number[];
};

export type PerspectiveWithSublevelTree = Perspective & {
  sublevels: PerspectiveSublevelWithChildren[];
};

export type AttachedTaskSummary = {
  id: number;
  createdAt: string;
};

export type PerspectivePortfolio = {
  [perspectiveId: number]: {
    id: number;
    directlyAttachedTasks: AttachedTaskSummary[];
    indirectlyAttachedTasks: AttachedTaskSummary[];
  };
};

export type RawPerspectiveSublevelPortfolio = {
  [perspectiveSublevelId: number]: {
    id: number;
    attachedTasks: AttachedTaskSummary[];
  };
};

export type PerspectiveSublevelAttachment = {
  directlyAttachedTasks: AttachedTaskSummary[];
  indirectlyAttachedTasks: AttachedTaskSummary[];
};

export type TaskSummaryWithChallengeUsers = TaskSummary & {
  challenge: {
    users: UserSummary[];
    invitees: UserSummary[];
  };
};

// TODO is this type really necessary?
export type UnprocessedAttachmentInfo = {
  directlyAttachedTasks: AttachedTaskSummary[];
  indirectlyAttachedTasks: AttachedTaskSummary[];
};

export type AttachmentInfo = {
  attachedTasksCount: number;
  attachedTasks: {
    all: Map<number, AttachedTaskSummary>;
    directlyAttached: Map<number, AttachedTaskSummary>;
    indirectlyAttached: Map<number, AttachedTaskSummary>;
  };
  fillLevel: number;
  fillFade: number;
};

// TODO rename to ProcessedPerspectiveSublevelPortfolio?
export type PerspectiveSublevelPortfolio = Map<
  number,
  PerspectiveSublevelAttachment
>;

export type PerspectiveAttachmentState = {
  directlyAttachedPerspectives: Record<number, PerspectiveSummary>;
  indirectlyAttachedPerspectives: Record<number, PerspectiveSummary>;
  attachedPerspectiveSublevels: Record<number, PerspectiveSublevelSummary>;
};

export type AttachmentResult = {
  directlyAttachedPerspectives: PerspectiveSummary[];
  indirectlyAttachedPerspectives: PerspectiveSummary[];
  attachedPerspectiveSublevels: PerspectiveSublevelSummary[];
};

export type Tag = {
  id: number;
  name: string;
  color: string | null;
  info: string | null;
  schoolId: number;
  createdAt: string;
  updatedAt: string;
};

export type OmitFramerProps<Props> = Omit<
  Props,
  'onDrag' | 'onDragStart' | 'onDragEnd' | 'onAnimationStart'
>;

export type Stylable<Props> = Props & {
  className?: string;
};

export type HTMLImgProps = DetailedHTMLProps<
  ImgHTMLAttributes<HTMLImageElement>,
  HTMLImageElement
>;

export type HTMLDivProps = DetailedHTMLProps<
  HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
>;

export type HTMLArticleProps = DetailedHTMLProps<
  HTMLAttributes<HTMLElement>,
  HTMLElement
>;

export type HTMLTableRowProps = DetailedHTMLProps<
  HTMLAttributes<HTMLTableRowElement>,
  HTMLTableRowElement
>;

export type HTMLTableCellProps = DetailedHTMLProps<
  HTMLAttributes<HTMLTableCellElement>,
  HTMLTableCellElement
>;

export type HTMLTableHeadCellProps = HTMLTableCellProps & {
  scope?: 'col' | 'row';
};

export type HTMLInputProps = DetailedHTMLProps<
  InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
>;

export type HTMLButtonProps = DetailedHTMLProps<
  ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
>;

export type HTMLSectionProps = DetailedHTMLProps<
  HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
>;

export type HTMLFormProps = DetailedHTMLProps<
  FormHTMLAttributes<HTMLFormElement>,
  HTMLFormElement
>;

export type HTMLSpanProps = DetailedHTMLProps<
  HTMLAttributes<HTMLSpanElement>,
  HTMLSpanElement
>;

export type ObjectWithId = {
  id: number;
};

export type ObjectWithName = {
  name: string;
};

export type ObjectWithPosition = {
  position: number;
};

export type ObjectWithGroups = {
  groups: { id: number }[];
};

export type ObjectWithAccessFields = ObjectWithGroups & {
  minimumRole: UserRole;
};

export type ObjectWithCreatedAt = {
  createdAt: string;
};

export type UpdatesOrUpdateFunction<T> =
  Partial<T> | ((original: T) => Partial<T>);

export type SelectProps<T> = Omit<MuiSelectProps<T>, 'variant'> & {
  variant?: SelectVariants;
};
